import type { RequestOptions } from '@@/plugin-request/request';
import { history, request, type RequestConfig } from '@umijs/max';
import { message as antMessage } from 'antd';
import { getItem, removeItem, saveItem } from './utils/storage';
import {
  AUTO_LOGIN_STORIGE_KEY,
  REFRESH_TOKEN_STORIGE_KEY,
  TOKEN_STORIGE_KEY,
  TOKEN_TYPE,
} from './constants';
import { getNewToken } from './services/common';

// 响应类型
enum RESPONSE_CODE {
  /** 成功 */
  SUCCESS = 200,
  /** 未登录 */
  NO_LOGIN = 401,
  /** 登录失效 */
  LOGIN_EXPIRE = 10014,
  /** 登录用户不存在 */
  LOGIN_USER_NOT_EXIST = 10404,
}
// 与后端约定的响应数据格式
interface Response {
  code: RESPONSE_CODE;
  data: any;
  detail: string;
  msg: string;
  message?: string;
}

// 是否正在刷新 Token
let isRefreshing = false;
// 存储待重发的请求
let pendingRequests: any[] = [];

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const requestConfig: RequestConfig = {
  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `${getItem(TOKEN_TYPE)} ${getItem(TOKEN_STORIGE_KEY)}`,
        },
      };
    },
  ],
  // 响应拦截器
  responseInterceptors: [
    async (response) => {
      // 拦截响应数据，进行个性化处理
      const { config, data: axiosData } = response;
      const blob = response.data as Blob;
      // 文件下载
      if (config.responseType === 'blob') {
        const disposition =
          response.headers['content-disposition'] || response.headers['Content-Disposition'];
        let fileName: any = '';
        if (disposition) {
          fileName =
            disposition
              ?.split(';')
              .filter(
                (item) =>
                  item.split('=')[0].trim() === 'fileName' ||
                  item.split('=')[0].trim() === 'filename',
              ) || [];
          fileName = decodeURIComponent(fileName[0] ? fileName[0].split('=')[1] : '');
        }
        // Excel 是二进制文件；只有错误响应才按 JSON 读取，避免下载成功后抛解析异常。
        const contentType = String(
          response.headers['content-type'] || response.headers['Content-Type'] || blob.type || '',
        );
        if (contentType.includes('json') || !fileName) {
          try {
            const text = await new Promise<string>((resolve, reject) => {
              const file = new FileReader();
              file.onload = () => resolve(String(file.result));
              file.onerror = () => reject(file.error);
              file.readAsText(blob, 'utf-8');
            });
            const obj = JSON.parse(text);
            antMessage.error(obj.message || obj.detail || obj.msg || '导出失败，请稍后重试');
          } catch {
            antMessage.error('未取得有效的导出文件，请稍后重试');
          }
          return response;
        }
        if (fileName) {
          antMessage.loading('正在下载...');
          if ((window.navigator as any).msSaveBlob) {
            (window.navigator as any).msSaveBlob(blob, fileName);
          } else {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          }

          antMessage.destroy();
          antMessage.success(`${fileName} 已下载完成`);
        }
        return response;
      }

      // 自定义错误处理逻辑
      const { skipErrorHandler } = config as RequestOptions;

      const { code, detail, msg, message } = axiosData as Response;

      // 登录失效，自动刷新token
      if (code === RESPONSE_CODE.LOGIN_EXPIRE && getItem(AUTO_LOGIN_STORIGE_KEY)) {
        if (!isRefreshing) {
          // 开始刷新 Token
          isRefreshing = true;
          // 调用刷新 Token 接口
          const refreshToken = getItem(REFRESH_TOKEN_STORIGE_KEY);
          const { code, data } = await getNewToken(refreshToken as string, {
            skipErrorHandler: true,
          });
          isRefreshing = false; // 重置刷新状态
          if (code === RESPONSE_CODE.SUCCESS) {
            // 更新Token
            const { access_token, token_type } = data;
            saveItem(TOKEN_TYPE, token_type);
            saveItem(TOKEN_STORIGE_KEY, access_token);
            // 将所有等待的请求重新发送
            pendingRequests.forEach((callback) => callback());
            pendingRequests = [];
            // 返回重新发送的请求
            return request(config.url as string, config);
          } else {
            // 刷新失败，清除 Token 并跳转到登录页
            removeItem(TOKEN_STORIGE_KEY);
            removeItem(TOKEN_STORIGE_KEY);
            antMessage.error('登录过期，请重新登录');
            // 清除 Token 并跳转到登录页
            // const { search, pathname } = window.location;
            history.replace({
              pathname: '/login',
              // search: stringify({
              //   redirect: pathname + search,
              // }),
            });
          }
        } else {
          // 已经在刷新 Token 的过程中，将请求加入等待队列中，等待刷新完成后再发送请求
          return new Promise((resolve) => {
            pendingRequests.push(() => {
              resolve(request(config.url as string, config));
            });
          });
        }
      }

      // 其他错误提示
      if (code !== RESPONSE_CODE.SUCCESS) {
        switch (code) {
          case RESPONSE_CODE.LOGIN_USER_NOT_EXIST:
          case RESPONSE_CODE.NO_LOGIN: {
            antMessage.warning('请先登录');
            // const { search, pathname } = window.location;
            history.replace({
              pathname: '/login',
              // search: stringify({
              //   redirect: pathname + search,
              // }),
            });
            break;
          }
          default:
            if (!skipErrorHandler) {
              antMessage.error(detail || msg || message || '请求失败，请稍后重试');
            }
        }
      }
      return response;
    },
  ],
  // 这里是针对网络错误处理
  errorConfig: {
    // 错误接收及处理
    errorHandler: (error: any, opts: RequestOptions) => {
      // 页面自行处理的错误只提示一次；未登录仍统一跳转登录页。
      if (opts?.skipErrorHandler && error.response?.status !== RESPONSE_CODE.NO_LOGIN) return;
      // 我们的 errorThrower 抛出的错误。
      if (error.response) {
        if (error.response.status !== 200) {
          if (error.response.status === RESPONSE_CODE.NO_LOGIN) {
            antMessage.warning(error.response?.data?.message || '请先登录');
            history.replace({
              pathname: '/login',
            });
          } else if (error.response.status === 504) {
            antMessage.error('后端服务响应超时，请稍后刷新重试');
          } else {
            antMessage.error(error.response?.data?.message || error.message);
          }
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        if (error?.message?.includes('timeout')) {
          antMessage.error('请求超时');
        } else {
          antMessage.error('系统错误');
        }
      } else {
        // 发送请求时出了点问题
        antMessage.error('请求失败');
      }
    },
  },
};
